import { useState } from 'react';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce/types/boolean';
import Alert from '@liquidweb-dev/undercurrent/Alert';
import AlertTitle from '@liquidweb-dev/undercurrent/AlertTitle';
import Button from '@liquidweb-dev/undercurrent/Button';
import Card from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import CardFooter from '@liquidweb-dev/undercurrent/CardFooter';
import CardHeader from '@liquidweb-dev/undercurrent/CardHeader';
import Checkbox from '@liquidweb-dev/undercurrent/Checkbox';
import FormControlLabel from '@liquidweb-dev/undercurrent/FormControlLabel';
import Note from '@liquidweb-dev/undercurrent/Note';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import LoaderCircleIcon from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import ArrowRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowRight';
import TriangleAlertIcon from '@liquidweb-dev/undercurrent/icons/lucide/TriangleAlert';
import { pseudoClasses } from '@liquidweb-dev/undercurrent/classes/pseudo';
import dayjs from 'dayjs';
import { AssetDetails } from 'modules/queries/asset/useDetails';
import useAssetRemove from 'modules/queries/asset/useRemove';
import useReportRelatedTerminationActions from 'modules/queries/asset/useReportRelatedTerminationActions';
import useServerShutdown from 'modules/queries/server/power/useShutdown';
import Addon from './Addon';
import AssetOverview from './AssetOverview';
import CancellationLoader from './CancellationLoader';

interface CancellationConfirmationProps {
	uniqId: string;
	assetDetails: AssetDetails | undefined;
	isAssetDetailsLoading: boolean;
	onClose: () => void;
	onScheduleCancellationSuccess: () => void;
	onDestroyServer: () => void;
}

const CancellationConfirmation = ({
	uniqId,
	assetDetails,
	isAssetDetailsLoading,
	onClose,
	onScheduleCancellationSuccess,
	onDestroyServer,
}: CancellationConfirmationProps) => {
	const [powerDownServer, setPowerDownServer] = useState(false);
	const { mutate: shutdownServer } = useServerShutdown();
	const { mutate: removeAsset, isLoading: isAssetRemoveLoading } =
		useAssetRemove();

	const { data: terminationActions } = useReportRelatedTerminationActions(
		{ uniq_id: uniqId },
		{ enabled: Boolean(uniqId) },
	);

	const { status, powerStatus, isPrivateParent } = assetDetails || {};
	const canScheduleTerminate = fromApiBoolean(
		assetDetails?.capabilities?.canScheduleTerminate,
	);

	/**
	 * A server that cannot have its termination scheduled is destroyed as soon
	 * as the customer confirms, so it ends today rather than at the next
	 * invoice.
	 */
	const cancellationDate = (
		canScheduleTerminate
			? dayjs(assetDetails?.nextInvoiceDate?.DateTime)
			: dayjs()
	).format('LL');

	const showPowerDownServerCheckbox =
		!isPrivateParent && status === 'Active' && powerStatus === 'on';

	const handleRemoveAsset = () => {
		if (powerDownServer) {
			shutdownServer({ uniq_id: uniqId });
		}
		removeAsset(
			{
				uniq_id: uniqId,
				cancellation_reason: 'Changed business direction',
				cleanup_assets: 0,
				schedule_terminate: 1,
			},
			{ onSuccess: () => onScheduleCancellationSuccess() },
		);
	};

	/**
	 * A server that cannot have its termination scheduled is destroyed as soon
	 * as the customer confirms, so its add-ons end today rather than at their
	 * next invoice.
	 */
	const getAddonCancellationDate = (
		addonUniqId: string,
	): string | undefined => {
		if (!canScheduleTerminate) {
			return dayjs().toISOString();
		}

		return assetDetails?.associationDetails?.find(
			(addon) => addon.other_uniq_id === addonUniqId,
		)?.next_invoice_date;
	};

	return (
		<Stack
			direction="column"
			sx={{
				justifyContent: 'center',
				alignItems: 'center',
				gap: 200,
			}}
		>
			<Stack
				direction="column"
				sx={{
					justifyContent: 'center',
					alignItems: 'center',
					gap: 400,
					alignSelf: 'stretch',
				}}
			>
				<Stack
					direction="column"
					sx={{ alignItems: 'flex-start', gap: 300, width: '100%' }}
				>
					<Alert
						severity="info"
						showIcon={false}
						slotProps={{
							message: {
								sx: { width: '100%' },
							},
						}}
					>
						{isAssetDetailsLoading ? (
							<Skeleton variant="rounded" />
						) : (
							<>
								<AlertTitle>
									Cancellation date:{' '}
									<Typography variant="body-md" sx={{ fontWeight: 700 }}>
										{cancellationDate}
									</Typography>
								</AlertTitle>
								{canScheduleTerminate && (
									<Typography variant="body-sm" sx={{ fontWeight: 400 }}>
										Service stays active until your billing cycle ends. No
										refund will be issued.
									</Typography>
								)}
							</>
						)}
					</Alert>
					<Card sx={{ width: '100%' }}>
						<CardHeader
							title={
								<Typography
									variant="label-lg"
									sx={{
										fontWeight: 500,
									}}
								>
									Selected Server
								</Typography>
							}
						/>
						<CardContent sx={{ display: 'flex', flexDirection: 'column' }}>
							{isAssetDetailsLoading ? (
								<CancellationLoader
									title="Fetching your details..."
									icon={<LoaderCircleIcon />}
								/>
							) : (
								<AssetOverview data={assetDetails} />
							)}
						</CardContent>
						<CardFooter
							sx={({ vars }) => ({
								backgroundColor: vars.palette.uc.warning.bg.main,
								border: `1px solid ${vars.palette.uc.warning.border.weak}`,
								display: 'flex',
								flexDirection: 'column',
								paddingX: 200,
								alignSelf: 'stretch',
								alignItems: 'flex-start',
								[pseudoClasses.important]: {
									paddingY: 100,
								},
							})}
						>
							<Note
								color="warning"
								variant="barebones"
								icon={<TriangleAlertIcon />}
							>
								Erased data is unrecoverable after the cancellation date
							</Note>
						</CardFooter>
					</Card>
					<FormControlLabel
						label="Power down the server now"
						description="Server goes offline immediately"
						control={
							<Checkbox
								name="powerDownServer"
								checked={powerDownServer}
								onChange={(e) => setPowerDownServer(e.target.checked)}
							/>
						}
						disabled={!showPowerDownServerCheckbox}
					/>
				</Stack>
				{terminationActions && terminationActions?.assets.length > 0 && (
					<Stack
						useFlexGap
						sx={{
							gap: 200,
							width: '100%',
						}}
					>
						<Stack
							direction="column"
							useFlexGap
							sx={{
								alignItems: 'flex-start',
								justifyContent: 'flex-start',
							}}
						>
							<Typography
								variant="body-sm"
								sx={{
									fontWeight: 500,
								}}
							>
								Add-ons cancelled with server
							</Typography>
							<Typography variant="body-sm">
								Here are the add-ons that you&apos;ll lose as part of this
								cancellation:
							</Typography>
						</Stack>
						<Stack
							direction="row"
							sx={{
								minWidth: '300px',
								alignContent: 'flex-start',
								flexWrap: 'wrap',
								rowGap: 150,
								columnGap: 300,
								paddingBottom: 150,
							}}
						>
							{terminationActions?.assets.map((action) => (
								<Addon
									key={action.uniq_id}
									data={action}
									cancellationDate={getAddonCancellationDate(action.uniq_id)}
								/>
							))}
						</Stack>
					</Stack>
				)}
			</Stack>
			<Stack direction="column" sx={{ gap: 400, width: '100%' }}>
				<Stack
					direction="column"
					sx={{
						alignItems: 'flex-start',
						gap: 100,
						alignSelf: 'stretch',
					}}
				>
					<Button
						size="large"
						variant="primary"
						sx={{
							paddingY: 100,
							paddingX: 200,
						}}
						onClick={canScheduleTerminate ? handleRemoveAsset : onDestroyServer}
						fullWidth
						loading={isAssetRemoveLoading || isAssetDetailsLoading}
					>
						{canScheduleTerminate ? 'Schedule cancellation' : 'Continue'}
					</Button>
					<Button
						size="large"
						variant="secondary"
						sx={{
							paddingY: 100,
							paddingX: 200,
						}}
						onClick={onClose}
						fullWidth
					>
						Keep service
					</Button>
				</Stack>
				{canScheduleTerminate && (
					<Button
						color="danger"
						variant="link"
						size="small"
						endIcon={<ArrowRightIcon />}
						onClick={() => {
							onDestroyServer();
							setPowerDownServer(false);
						}}
						sx={{ textDecoration: 'none' }}
					>
						Destroy now instead
					</Button>
				)}
			</Stack>
		</Stack>
	);
};

export default CancellationConfirmation;
